import {
  ColumnDef,
  flexRenderComponent,
} from '@3ddv/software-division-components/backoffice/table';
import { BadgeVariant } from '@3ddv/software-division-components/backoffice/badge';
import { GsBadgeCellComponent } from './cells/gs-badge-cell.component';
import { GsImageCellComponent } from './cells/gs-image-cell.component';
import {
  GsAction,
  GsActionCellComponent,
  GsActionTone,
} from './cells/gs-action-cell.component';

export interface GsBadgeColumnConfig<T> {
  id: string;
  header: string;
  text: (row: T) => string;
  variant: (row: T) => BadgeVariant;
  dataCy?: string | ((row: T) => string);
  enableSorting?: boolean;
}

export function gsBadgeColumn<T extends Record<string, any>>(
  config: GsBadgeColumnConfig<T>
): ColumnDef<T> {
  return {
    id: config.id,
    header: config.header,
    accessorFn: row => config.text(row),
    enableSorting: config.enableSorting ?? false,
    cell: context =>
      flexRenderComponent(GsBadgeCellComponent, {
        inputs: {
          text: config.text(context.row.original),
          variant: config.variant(context.row.original),
          ...(config.dataCy && {
            dataCy:
              typeof config.dataCy === 'function'
                ? config.dataCy(context.row.original)
                : config.dataCy,
          }),
        },
      }),
  };
}

export interface GsImageColumnConfig<T> {
  id?: string;
  header?: string;
  image: (row: T) => string | null | undefined;
  previewOnHover?: boolean;
}

export function gsImageColumn<T extends Record<string, any>>(
  config: GsImageColumnConfig<T>
): ColumnDef<T> {
  return {
    id: config.id ?? 'image',
    header: config.header ?? '',
    enableSorting: false,
    cell: context =>
      flexRenderComponent(GsImageCellComponent, {
        inputs: {
          src: config.image(context.row.original) || null,
          previewOnHover: config.previewOnHover ?? false,
        },
      }),
  };
}

export interface GsRowAction<T> {
  id: string;
  label: string | ((row: T) => string);
  destructive?: boolean;
  tone?: GsActionTone | ((row: T) => GsActionTone);
  dataCy: string | ((row: T) => string);
  indicator?: (row: T) => boolean;
}

export interface GsActionsColumnConfig<T> {
  id?: string;
  header?: string;
  actions: GsRowAction<T>[];
  onSelect: (actionId: string, row: T) => void;
  rowDataCy?: string | ((row: T) => string);
}

export function gsActionsColumn<T extends Record<string, any>>(
  config: GsActionsColumnConfig<T>
): ColumnDef<T> {
  return {
    id: config.id ?? 'actions',
    header: config.header ?? '',
    enableSorting: false,
    cell: context => {
      const row = context.row.original;
      const actions: GsAction[] = config.actions.map(action => ({
        id: action.id,
        label:
          typeof action.label === 'function' ? action.label(row) : action.label,
        variant: action.destructive ? 'destructive' : 'secondary',
        ...(action.tone && {
          tone:
            typeof action.tone === 'function' ? action.tone(row) : action.tone,
        }),
        dataCy:
          typeof action.dataCy === 'function'
            ? action.dataCy(row)
            : action.dataCy,
        ...(action.indicator && { indicator: action.indicator(row) }),
      }));

      return flexRenderComponent(GsActionCellComponent, {
        inputs: {
          actions,
          ...(config.rowDataCy && {
            rowDataCy:
              typeof config.rowDataCy === 'function'
                ? config.rowDataCy(row)
                : config.rowDataCy,
          }),
        },
        outputs: {
          actionClick: (actionId: string) => config.onSelect(actionId, row),
        },
      });
    },
  };
}
